// src/features/analytics/components/MediaHourlyTrendCard.tsx
//
// Hour-grain trend for a short event window (dashboard ADR-009): hours are
// points in time, so the axis is the viewer's local time; missing hours are
// zero-filled across the effective window for axis continuity only.

import { useState } from 'react';
import { Group, Paper, SegmentedControl, Skeleton, Text, Title } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import {
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { AnalyticsUtils } from '../AnalyticsUtils';
import type { DayZone } from '@/shared/utils/utcRange';
import {
  axisTickLabel,
  fillHourlyGaps,
  hourMidpoints,
  hourSpanLabel,
  hourTicks,
  snapWindowToHours,
  windowSpansDays,
  withZeroEdges,
  type EventWindow,
} from '../eventWindow';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type { AnalyticsEnvelope, PlaybackHourlyData, PlaybackHourlyPoint } from '../types';

interface MediaHourlyTrendCardProps {
  hourly: AnalyticsEnvelope<PlaybackHourlyData> | null;
  window: EventWindow;
  isLoading: boolean;
  isError: boolean;
  /** Plain line for a window with no viewers (today so far, or any window past the last data hour). */
  emptyCopy?: string;
  /** Zone for axis and tooltip labels (the page's local-day zone). */
  zone?: DayZone;
  error: Error | null;
  onRetry: () => void;
}

interface HourChartRow {
  /** Plot position, epoch seconds: the hour's midpoint (numeric x axis shared with the concurrency chart). */
  x: number;
  /** Bucket start, epoch seconds (the tooltip's hour span). */
  hour: number;
  /** A zero-value point placed on the domain edge so the curve meets the boundary honestly (no tooltip). */
  edge?: boolean;
  value: number;
  sessions: number;
  watchTimeMs: number;
  uniqueViewers: number;
}

function chartValue(point: PlaybackHourlyPoint, metric: AnalyticsMetric): number {
  if (metric === 'sessions') return point.sessions;
  if (metric === 'uniqueViewers') return point.uniqueViewers;
  return Math.round(point.watchTimeMs / 60000);
}

function HourTooltip({ payload, zone, multiDay }: { payload?: unknown; zone: DayZone; multiDay: boolean }) {
  const rows = payload as { payload?: HourChartRow }[] | undefined;
  const row = rows?.[0]?.payload;
  if (!row || row.edge) return null;
  return (
    <Paper withBorder shadow="sm" p="xs">
      <Text size="xs" fw={600}>
        {hourSpanLabel(row.hour, multiDay, zone)} (local)
      </Text>
      <Text size="xs">Sessions: {AnalyticsUtils.formatCount(row.sessions)}</Text>
      <Text size="xs">Watch time: {AnalyticsUtils.formatWatchTime(row.watchTimeMs)}</Text>
      <Text size="xs">Unique viewers: {AnalyticsUtils.formatCount(row.uniqueViewers)}</Text>
    </Paper>
  );
}

export function MediaHourlyTrendCard({
  hourly,
  window,
  isLoading,
  isError,
  error,
  onRetry,
  emptyCopy = 'No viewing data in this window.',
  zone = 'local',
}: MediaHourlyTrendCardProps) {
  const [metric, setMetric] = useState<AnalyticsMetric>('sessions');
  const points = hourly?.data.points ?? [];
  // The EFFECTIVE window (the gateway's outward hour snap, mirrored client-side) is the
  // shared x domain of both time charts (ADR-009 amendment).
  const domain = snapWindowToHours(window);
  const multiDay = windowSpansDays(domain, zone);
  const filled = fillHourlyGaps(points, domain);
  // Smooth curve: each bucket sits at the MIDDLE of its hour; zero edges at the window
  // bounds only when the outer buckets are zero anyway (no fabricated non-zero edges).
  const chartData: HourChartRow[] = withZeroEdges(hourMidpoints(filled), domain.from, domain.to).map((p) => ({
    x: p.x,
    hour: p.hour,
    value: chartValue(p, metric),
    sessions: p.sessions,
    watchTimeMs: p.watchTimeMs,
    uniqueViewers: p.uniqueViewers,
    edge: p.edge,
  }));

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="xs" wrap="wrap">
        <Title order={5}>Views by hour</Title>
        {points.length > 0 && (
          <SegmentedControl
            size="xs"
            data={ANALYTICS_METRICS.map((m) => ({ value: m, label: ANALYTICS_METRIC_LABELS[m] }))}
            value={metric}
            onChange={(value) => setMetric(value as AnalyticsMetric)}
          />
        )}
      </Group>
      {isLoading ? (
        <Skeleton height={220} />
      ) : isError ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : points.length === 0 ? (
        AnalyticsUtils.coverageIsEmpty(hourly?.coverage, hourly?.freshness, window) ? (
          <AnalyticsCoverageNotice
            coverage={hourly?.coverage}
            freshness={hourly?.freshness}
            range={window}
            emptyMessage={emptyCopy}
          />
        ) : (
          <>
            <AnalyticsCoverageNotice coverage={hourly?.coverage} freshness={hourly?.freshness} range={window} />
            <Text size="sm" c="dimmed" py="md">
              {emptyCopy}
            </Text>
          </>
        )
      ) : (
        <>
          <AreaChart
            h={220}
            data={chartData}
            dataKey="x"
            series={[
              {
                name: 'value',
                label: metric === 'watchTime' ? 'Watch time (min)' : ANALYTICS_METRIC_LABELS[metric],
                color: 'blue.6',
              },
            ]}
            curveType="monotone"
            withDots={false}
            xAxisProps={{
              type: 'number',
              domain: [domain.from, domain.to],
              ticks: hourTicks(domain),
              interval: 0,
              tickFormatter: (v: number) => axisTickLabel(v, multiDay, zone),
            }}
            tooltipProps={{
              content: ({ payload }) => <HourTooltip payload={payload} zone={zone} multiDay={multiDay} />,
            }}
          />
          <Text size="xs" c="dimmed" mt={4}>
            Each point is one hour: {ANALYTICS_METRIC_LABELS[metric].toLowerCase()} active at any
            point in that hour, so a session spanning two hours counts in both. The concurrency
            chart below shows viewers minute by minute. Times are in your local timezone.
          </Text>
        </>
      )}
    </Paper>
  );
}
